<template>
  <button
    v-if="isMobile"
    class="btn btn-outlined open-filters"
    @click="openFilters"
  >
    Filter
  </button>
  <teleport
    to="body"
    :disabled="!isMobile"
  >
    <Transition
      :name="mobileTransition"
      type="animation"
    >
      <aside
        v-show="isSidebarVisible"
        class="search-sidebar"
        :class="isOpenFilters ? 'mobile-open' : 'mobile-hide'"
      >
        <div :class="isMobile ? 'container mobile-container' : 'desktop-container'">
          <div
            v-if="isMobile"
            class="mobile-header"
          >
            <h3 class="text-title-lg">
              Filter
            </h3>
            <button
              class="close-filters"
              @click="closeFilters"
            >
              <i
                class="icon-sm icon-reg icon-close"
                aria-hidden="true"
              />
              <span class="sr-only">Close Filters</span>
            </button>
          </div>
          <div :class="isMobile ? 'mobile-scrollable' : 'desktop-inner-container sticky top-5'">
            <ais-search-box>
              <template #default="{ currentRefinement, isSearchStalled, refine }">
                <form
                  :id="'search-form-' + searchType"
                  role="search"
                  class="search-form"
                  novalidate
                  @submit.prevent=""
                >
                  <label
                    :for="'search-input-' + searchType"
                    class="sr-only"
                  >Search</label>
                  <debounced-search 
                    :placeholder="placeholder"
                  />
                  <i
                    v-if="currentRefinement.length === 0"
                    class="icon-sm icon-reg icon-search"
                    aria-hidden="true"
                  />
                  <button
                    v-else
                    class="search-clear"
                    type="reset"
                    title="Clear"
                    aria-label="Clear"
                    @click="refine()"
                  >
                    <i
                      class="icon-sm icon-reg icon-close"
                      aria-hidden="true"
                    />
                  </button>
                  <span
                    class="search-loading"
                    aria-label="Loading"
                    :hidden="!isSearchStalled"
                    :aria-hidden="!isSearchStalled"
                  >
                    Loading...
                  </span>
                </form>
              </template>
            </ais-search-box>
            <ais-clear-refinements
              v-if="!isMobile"
              class="desktop-clear"
            >
              <template #default="{ canRefine, refine }">
                <button
                  v-if="canRefine"
                  @click.prevent="refine"
                >
                  Clear Refinements
                </button>
              </template>
            </ais-clear-refinements>
            <ais-dynamic-widgets>
              <filter-widget
                attribute="media_type"
                title="Media Type"
              />
              <filter-widget
                attribute="cat_names"
                :show-more="true"
                :show-more-limit="20"
                title="Categories"
              />
            </ais-dynamic-widgets>
          </div>
          <div
            v-if="isMobile"
            class="filter-buttons"
          >
            <ais-clear-refinements class="button-wrap">
              <template #default="{ refine }">
                <button
                  class="btn btn-outlined clear-filters"
                  @click.prevent="refine"
                >
                  Clear
                </button>
              </template>
            </ais-clear-refinements>
            <div class="button-wrap">
              <button
                class="btn show-results"
                @click="closeFilters"
              >
                Show Results
              </button>
            </div>
          </div>
        </div>
      </aside>
    </Transition>
  </teleport>
</template>

<script>
/* Components */
import {
  AisSearchBox,
  AisClearRefinements,
  AisDynamicWidgets,
} from 'vue-instantsearch/vue3/es';
import DebouncedSearch from '../DebouncedSearch.vue';
import FilterWidget from './FilterWidget.vue';

/* Utilities */
import debounce from '../../utilities/debounce';
import isMobile from '../../utilities/isMobile';

export default {
  components: {
    AisSearchBox,
    AisClearRefinements,
    AisDynamicWidgets,
    DebouncedSearch,
    FilterWidget,
  },

  inject: {
    searchType: {
      default: 'global',
    },
  },

  data() {
    return {
      isMobile: undefined,
      isOpenFilters: false,
      isSidebarVisible: undefined,
      placeholder: 'Search for a topic',
    };
  },

  computed: {
    mobileTransition() {
      return this.isMobile && this.isOpenFilters ? 'slide' : null;
    },
  },

  mounted() {
    window.addEventListener('resize', debounce(this.afterResize, 250));
    this.isMobile = isMobile(window.innerWidth);
    this.placeholder = this.setPlaceholder();
    this.$nextTick(() => this.isSidebarVisible = !this.isMobile);
  },

  unmounted() {
    window.removeEventListener('resize', debounce(this.afterResize, 250));
  },

  methods: {
    afterResize() {
      this.isMobile = isMobile(window.innerWidth);
      this.isSidebarVisible = !this.isMobile;
      this.isOpenFilters = false;
      document.body.classList.remove('no-scroll');
    },

    closeFilters() {
      this.isSidebarVisible = false;
      setTimeout(() => this.isOpenFilters = false, 750);
      setTimeout(() => document.body.classList.remove('no-scroll'), 750);
    },

    openFilters() {
      this.isSidebarVisible = true;
      this.isOpenFilters = true;
      setTimeout(() => document.body.classList.add('no-scroll'), 750);
    },

    /**
     * Set placeholder text for pinned posts or default.
     * 
     * @return string
     */
    setPlaceholder() {
      if (this.searchType === 'pinned-posts') {
        return this.placeholder = 'Search pinned resources';
      }

      return this.placeholder;
    },
  },
};
</script>
